import React, { useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import type { Group } from 'three';
import { environments, celebrations } from '../core/registries';
import { samplePolyline, type SplinePose } from '../core/splineDrive/useSplineDrive';
import type { BinColor, SoppelLevel, SoppelStop } from '../../data/schemas';
import { TruckModel } from './TruckModel';
import { BinMesh, type BinDumpState, type BinVisualState } from './BinMesh';
import { LitterMesh } from './LitterMesh';
import { EnvironmentDecor } from './EnvironmentDecor';
import { WebGlErrorBoundary } from './WebGlErrorBoundary';
import { followCamDesired, noRaycast } from './finishManeuver';
import { dumpPhase, hopperHoverWorld } from './dumpAnimation';
import { createSafeCanvasEvents } from './safeCanvasEvents';

export interface BinRuntime {
  stopIndex: number;
  binIndex: number;
  color: BinColor;
  target: boolean;
  visual: BinVisualState;
  surprise?: string;
  shownSurprise?: boolean;
}

export interface LitterRuntime {
  id: string;
  stopIndex: number;
  position: [number, number, number];
  collected: boolean;
}

export interface SoppelSceneProps {
  level: SoppelLevel;
  pose: SplinePose;
  routePoints: [number, number][];
  signColor: BinColor | null;
  armLift: number;
  /** The bin currently flying to the truck (armLift is its 0..1 dump progress). */
  dumpingBin?: { stopIndex: number; binIndex: number } | null;
  dumping: boolean;
  bins: BinRuntime[];
  litter: LitterRuntime[];
  celebrating: boolean;
  activeStopIndex: number | null;
  stopPhase: 'litter' | 'bins' | 'done' | 'driving';
  loadAmount: number;
  /** start | play | finish */
  worldPhase: 'start' | 'play' | 'finish';
  garageDoorOpen: number;
  finishPreset: string;
  tipDump: boolean;
  headlightsBlink: boolean;
  truckPulse: boolean;
  truckTapEnabled: boolean;
  /** When false, stop the render loop (e.g. world kept mounted under reward). */
  canvasActive?: boolean;
  onTruckTap: () => void;
  onBinTap: (stopIndex: number, binIndex: number) => 'ok' | 'wrong' | 'progress';
  onLitterTap: (id: string) => void;
}

function FollowCam({ target, worldPhase }: { target: SplinePose; worldPhase: string }) {
  useFrame(({ camera }) => {
    const desired = followCamDesired(target, worldPhase);
    camera.position.x += (desired.x - camera.position.x) * 0.06;
    camera.position.y += (desired.y - camera.position.y) * 0.06;
    camera.position.z += (desired.z - camera.position.z) * 0.06;
    camera.lookAt(target.x, 1.2, target.z);
  });
  return null;
}

function CelebrationBurst({ celebrationId }: { celebrationId: string }) {
  const group = useRef<Group>(null);
  const def = celebrations.get(celebrationId);
  useFrame((_, dt) => {
    if (group.current) group.current.rotation.y += dt * 0.8;
  });
  return (
    <group ref={group} name={`celebration-${celebrationId}`} position={[0, 3, 0]}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <mesh
          key={i}
          position={[
            Math.cos((i / 6) * Math.PI * 2) * 2.5,
            Math.sin(i) * 0.5,
            Math.sin((i / 6) * Math.PI * 2) * 2.5,
          ]}
        >
          <sphereGeometry args={[0.25, 10, 10]} />
          <meshStandardMaterial color={i % 2 ? '#FFE66D' : '#F9B233'} emissive="#FFE66D" emissiveIntensity={0.4} />
        </mesh>
      ))}
      <mesh visible={false} name={`celebration-label-${def.label}`} />
    </group>
  );
}

function Road({ points }: { points: [number, number][] }) {
  return (
    <group name="road">
      {points.map((p, i) => {
        if (i === 0) return null;
        const prev = points[i - 1];
        const mid: [number, number, number] = [(prev[0] + p[0]) / 2, 0.02, (prev[1] + p[1]) / 2];
        const len = Math.hypot(p[0] - prev[0], p[1] - prev[1]);
        const yaw = Math.atan2(p[1] - prev[1], p[0] - prev[0]);
        return (
          <group key={i} position={mid} rotation={[0, -yaw, 0]}>
            <mesh position={[0, 0.01, 0]} receiveShadow>
              <boxGeometry args={[len + 0.6, 0.04, 4.6]} />
              <meshStandardMaterial color="#E8D5B7" roughness={0.9} />
            </mesh>
            <mesh position={[0, 0.035, 0]} receiveShadow>
              <boxGeometry args={[len + 0.5, 0.06, 3.3]} />
              <meshStandardMaterial color="#7A8494" roughness={0.85} />
            </mesh>
            <mesh position={[0, 0.07, 0]}>
              <boxGeometry args={[Math.max(0.6, len * 0.35), 0.02, 0.14]} />
              <meshStandardMaterial color="#FFE66D" roughness={0.6} />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}

/** Depot garage at the start of the road — sized to fit the toy truck (~3.6×1.7×2.2). */
function DepotGarage({
  routePoints,
  doorOpen,
}: {
  routePoints: [number, number][];
  doorOpen: number;
}) {
  const start = routePoints[0] ?? [-12, 0];
  const next = routePoints[1] ?? [start[0] + 1, start[1]];
  const yaw = Math.atan2(next[1] - start[1], next[0] - start[0]);
  // Sit just behind the start point; keep scale close to the truck
  const bx = start[0] - Math.cos(yaw) * 3.0;
  const bz = start[1] - Math.sin(yaw) * 3.0;
  const doorH = 1.85 * (1 - doorOpen);

  return (
    <group name="depot-garage" position={[bx, 0, bz]} rotation={[0, -yaw, 0]}>
      <mesh name="garage-floor" position={[0, 0.05, 0]} receiveShadow raycast={noRaycast}>
        <boxGeometry args={[4.0, 0.08, 2.9]} />
        <meshStandardMaterial color="#9CA3AF" roughness={0.9} />
      </mesh>
      <mesh name="garage-wall-l" position={[0, 1.1, 1.4]} castShadow raycast={noRaycast}>
        <boxGeometry args={[3.7, 2.15, 0.18]} />
        <meshStandardMaterial color="#F97316" roughness={0.55} />
      </mesh>
      <mesh name="garage-wall-r" position={[0, 1.1, -1.4]} castShadow raycast={noRaycast}>
        <boxGeometry args={[3.7, 2.15, 0.18]} />
        <meshStandardMaterial color="#F97316" roughness={0.55} />
      </mesh>
      <mesh name="garage-back" position={[-1.85, 1.1, 0]} castShadow raycast={noRaycast}>
        <boxGeometry args={[0.18, 2.15, 2.9]} />
        <meshStandardMaterial color="#EA580C" roughness={0.55} />
      </mesh>
      <mesh name="garage-roof" position={[0, 2.25, 0]} castShadow raycast={noRaycast}>
        <boxGeometry args={[4.05, 0.18, 3.1]} />
        <meshStandardMaterial color="#FB923C" roughness={0.5} />
      </mesh>
      {/* Roll-up door at the front (+X toward road) */}
      <mesh
        name="garage-door"
        position={[1.85, doorH / 2 + 0.08, 0]}
        castShadow
        visible={doorH > 0.05}
        raycast={noRaycast}
      >
        <boxGeometry args={[0.1, doorH, 2.6]} />
        <meshStandardMaterial color="#64748B" metalness={0.3} roughness={0.45} />
      </mesh>
      <mesh name="garage-sign" position={[1.95, 2.05, 0]} raycast={noRaycast}>
        <boxGeometry args={[0.08, 0.32, 1.4]} />
        <meshStandardMaterial color="#FFE66D" emissive="#FFE66D" emissiveIntensity={0.25} />
      </mesh>
    </group>
  );
}

/** Distance from the route end where the truck stops at the plant (rear wheels short of the pit). */
export const PLANT_STOP_DIST = 2.6;

/** World pose of the finish landmark (plant / parade) for the dump approach. */
export function finishLandmarkWorld(
  routePoints: [number, number][],
  preset: string
): { x: number; z: number; yaw: number } {
  const end = routePoints[routePoints.length - 1] ?? [12, 0];
  const prev = routePoints[routePoints.length - 2] ?? [end[0] - 1, end[1]];
  const yaw = Math.atan2(end[1] - prev[1], end[0] - prev[0]);
  // Plant: stop with the rear wheels just short of the pit's orange ring (ring starts
  // ~4.55 from the route end; rear wheels sit ~1.7 behind the truck origin).
  const dist = preset === 'parade-square' ? 4.5 : PLANT_STOP_DIST;
  return {
    x: end[0] + Math.cos(yaw) * dist,
    z: end[1] + Math.sin(yaw) * dist,
    yaw,
  };
}

/** Recycling plant / parade square at the far end — visible during the whole drive. */
function FinishLandmark({
  routePoints,
  preset,
}: {
  routePoints: [number, number][];
  preset: string;
}) {
  const isParade = preset === 'parade-square';
  const isNone = preset === 'recycling-center-exit';
  if (isNone) return null;

  // Landmark origin sits a bit further than the dump stop so the truck drives inside
  const end = routePoints[routePoints.length - 1] ?? [12, 0];
  const prev = routePoints[routePoints.length - 2] ?? [end[0] - 1, end[1]];
  const yaw = Math.atan2(end[1] - prev[1], end[0] - prev[0]);
  const fx = end[0] + Math.cos(yaw) * 5.5;
  const fz = end[1] + Math.sin(yaw) * 5.5;

  return (
    <group
      name={isParade ? 'parade-square' : 'recycling-plant'}
      position={[fx, 0, fz]}
      rotation={[0, -yaw, 0]}
    >
      {isParade ? (
        <>
          <mesh position={[0, 0.05, 0]} receiveShadow raycast={noRaycast}>
            <boxGeometry args={[10, 0.1, 10]} />
            <meshStandardMaterial color="#E8D5B7" />
          </mesh>
          <mesh position={[0, 2.5, -3]} castShadow raycast={noRaycast}>
            <boxGeometry args={[6, 5, 1.5]} />
            <meshStandardMaterial color="#EF4444" />
          </mesh>
          <mesh position={[-2, 4, 2]} raycast={noRaycast}>
            <boxGeometry args={[0.15, 3, 0.15]} />
            <meshStandardMaterial color="#FFFFFF" />
          </mesh>
          <mesh position={[-2, 5.2, 2]} raycast={noRaycast}>
            <boxGeometry args={[1.2, 0.7, 0.08]} />
            <meshStandardMaterial color="#EF4444" />
          </mesh>
        </>
      ) : (
        <>
          {/* Open recycling pad: just a floor and the pit — no walls or roof */}
          <mesh name="plant-floor" position={[0, 0.03, 0]} receiveShadow raycast={noRaycast}>
            <boxGeometry args={[8, 0.12, 7]} />
            <meshStandardMaterial color="#A8A29E" roughness={0.9} />
          </mesh>
          {/* Large pit */}
          <mesh name="plant-pit" position={[1.5, 0.02, 0]} receiveShadow raycast={noRaycast}>
            <cylinderGeometry args={[2.2, 2.0, 0.6, 20]} />
            <meshStandardMaterial color="#1F2937" roughness={0.95} />
          </mesh>
          <mesh
            name="plant-pit-rim"
            position={[1.5, 0.35, 0]}
            rotation={[-Math.PI / 2, 0, 0]}
            raycast={noRaycast}
          >
            <ringGeometry args={[2.0, 2.45, 20]} />
            <meshStandardMaterial color="#F97316" />
          </mesh>
        </>
      )}
    </group>
  );
}

/** Small dust puffs at the truck's rear while the hopper tips out. */
function DustPuff({ active }: { active: boolean }) {
  const group = useRef<Group>(null);
  useFrame((_, dt) => {
    if (!group.current || !active) return;
    group.current.position.y += dt * 0.5;
    group.current.scale.multiplyScalar(1 + dt * 0.35);
  });
  if (!active) return null;
  return (
    <group ref={group} name="dump-dust" position={[0, 0.3, 0]}>
      {[0, 1, 2, 3].map((i) => (
        <mesh key={i} position={[Math.cos(i * 1.7) * 0.55, 0.12 * i, Math.sin(i * 1.7) * 0.55]}>
          <sphereGeometry args={[0.2, 8, 8]} />
          <meshStandardMaterial color="#D6D3D1" transparent opacity={0.4} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

function stopWorldPos(
  stop: SoppelStop,
  routePoints: [number, number][]
): { bins: [number, number, number][]; litter: [number, number, number][] } {
  const pose = samplePolyline(routePoints, stop.t);
  const side = stop.side === 'right' ? 1 : -1;
  // Pavement side of the road (on sidewalk band), not on grass
  const lateral = 2.55 * side;
  const nx = Math.cos(pose.yaw + Math.PI / 2) * lateral;
  const nz = Math.sin(pose.yaw + Math.PI / 2) * lateral;
  const binPositions: [number, number, number][] = stop.bins.map((_, i) => {
    const along = (i - (stop.bins.length - 1) / 2) * 1.4;
    return [
      pose.x + nx + Math.cos(pose.yaw) * along,
      0,
      pose.z + nz + Math.sin(pose.yaw) * along,
    ];
  });
  const litterCount = stop.litter ?? 0;
  const litterPositions: [number, number, number][] = Array.from({ length: litterCount }, (_, i) => {
    const a = (i / Math.max(1, litterCount)) * Math.PI - Math.PI / 2;
    return [
      pose.x + nx + Math.cos(a) * 1.6,
      0,
      pose.z + nz + Math.sin(a) * 1.2 + (i % 2) * 0.4,
    ];
  });
  return { bins: binPositions, litter: litterPositions };
}

function SceneInner(props: SoppelSceneProps) {
  const env = environments.get(props.level.scene.environment);
  const stopLayouts = useMemo(
    () => props.level.stops.map((stop) => stopWorldPos(stop, props.routePoints)),
    [props.level.stops, props.routePoints]
  );

  const night = props.level.scene.timeOfDay === 'night' || props.level.scene.timeOfDay === 'evening';

  return (
    <>
      <color attach="background" args={[env.sky]} />
      <hemisphereLight args={[env.sky, env.ground, night ? 0.55 : 0.9]} />
      <ambientLight intensity={night ? 0.45 : 0.65} />
      <directionalLight
        position={[8, 14, 6]}
        intensity={night ? 0.7 : 1.15}
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
      />
      <mesh
        name={`env-${props.level.scene.environment}`}
        rotation={[-Math.PI / 2, 0, 0]}
        receiveShadow
        position={[0, 0, 0]}
      >
        <planeGeometry args={[140, 140]} />
        <meshStandardMaterial color={env.ground} roughness={0.95} />
      </mesh>
      <EnvironmentDecor
        env={env}
        environmentId={props.level.scene.environment}
        routePoints={props.routePoints}
        night={night}
      />
      <Road points={props.routePoints} />
      <DepotGarage routePoints={props.routePoints} doorOpen={props.garageDoorOpen} />
      <FinishLandmark routePoints={props.routePoints} preset={props.finishPreset} />
      <FollowCam target={props.pose} worldPhase={props.worldPhase} />
      <TruckModel
        pose={props.pose}
        signColor={props.signColor}
        armLift={0}
        dumping={props.dumping}
        loadAmount={props.loadAmount}
        lidOpen={props.dumpingBin ? dumpPhase(props.armLift).lid : 0}
        dumpT={props.dumpingBin ? props.armLift : 0}
        dumpColor={
          props.dumpingBin
            ? props.bins.find(
                (b) =>
                  b.stopIndex === props.dumpingBin!.stopIndex &&
                  b.binIndex === props.dumpingBin!.binIndex
              )?.color ?? null
            : null
        }
        pulse={props.truckPulse}
        headlightsBlink={props.headlightsBlink}
        tipDump={props.tipDump}
        tapEnabled={props.truckTapEnabled}
        onTap={props.onTruckTap}
      />
      {props.tipDump ? (
        <group
          position={[
            props.pose.x - Math.cos(props.pose.yaw) * 2.6,
            0,
            props.pose.z - Math.sin(props.pose.yaw) * 2.6,
          ]}
        >
          <DustPuff active />
        </group>
      ) : null}

      {props.bins.map((bin) => {
        const layout = stopLayouts[bin.stopIndex]?.bins[bin.binIndex];
        if (!layout) return null;
        const active = props.activeStopIndex === bin.stopIndex;
        const highlight =
          active &&
          props.stopPhase !== 'litter' &&
          bin.target &&
          bin.visual !== 'emptied';
        const isDumping =
          props.dumpingBin != null &&
          props.dumpingBin.stopIndex === bin.stopIndex &&
          props.dumpingBin.binIndex === bin.binIndex;
        let dump: BinDumpState | null = null;
        if (isDumping && props.armLift > 0) {
          const hover = hopperHoverWorld(props.pose, [layout[0], layout[2]]);
          dump = {
            t: props.armLift,
            dx: hover.x - layout[0],
            dz: hover.z - layout[2],
            dirX: hover.dirX,
            dirZ: hover.dirZ,
          };
        }
        return (
          <BinMesh
            key={`bin-${bin.stopIndex}-${bin.color}-${bin.binIndex}`}
            dump={dump}
            name={`bin-${bin.stopIndex}-${bin.color}`}
            color={bin.color}
            position={layout}
            visual={bin.visual}
            highlight={highlight}
            surprise={bin.shownSurprise ? bin.surprise ?? null : null}
            onTap={() => (active ? props.onBinTap(bin.stopIndex, bin.binIndex) : 'wrong')}
          />
        );
      })}

      {props.litter.map((item) => (
        <LitterMesh
          key={item.id}
          name={item.id}
          position={item.position}
          collected={item.collected}
          highlight={
            props.activeStopIndex === item.stopIndex &&
            props.stopPhase === 'litter' &&
            !item.collected
          }
          onTap={() => {
            if (props.activeStopIndex === item.stopIndex) props.onLitterTap(item.id);
          }}
        />
      ))}

      {props.celebrating ? <CelebrationBurst celebrationId={props.level.reward.celebration} /> : null}
    </>
  );
}

export function buildInitialLitter(
  level: SoppelLevel,
  routePoints: [number, number][]
): LitterRuntime[] {
  const items: LitterRuntime[] = [];
  level.stops.forEach((stop, stopIndex) => {
    const layout = stopWorldPos(stop, routePoints);
    layout.litter.forEach((pos, i) => {
      items.push({
        id: `litter-${stopIndex}-${i}`,
        stopIndex,
        position: pos,
        collected: false,
      });
    });
  });
  return items;
}

export function buildInitialBins(level: SoppelLevel): BinRuntime[] {
  const bins: BinRuntime[] = [];
  level.stops.forEach((stop, stopIndex) => {
    stop.bins.forEach((bin, binIndex) => {
      let visual: BinVisualState = 'ready';
      if (bin.state === 'tipped') visual = 'tipped';
      if (bin.state === 'snowy') visual = 'snowy';
      bins.push({
        stopIndex,
        binIndex,
        color: bin.color,
        target: bin.target,
        visual,
        surprise: bin.surprise,
        shownSurprise: false,
      });
    });
  });
  return bins;
}

export const SoppelScene: React.FC<SoppelSceneProps> = (props) => {
  return (
    <WebGlErrorBoundary>
      <div
        className="h-full w-full"
        data-name="soppel-canvas-host"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', minHeight: '100%' }}
      >
        <Canvas
          shadows
          dpr={[1, 2]}
          frameloop={props.canvasActive === false ? 'never' : 'always'}
          camera={{ position: [0, 8, 12], fov: 45, near: 0.1, far: 200 }}
          style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none' }}
          gl={{ antialias: true, powerPreference: 'default', failIfMajorPerformanceCaveat: false }}
          resize={{ scroll: false, debounce: 0 }}
          events={createSafeCanvasEvents}
        >
          <SceneInner {...props} />
        </Canvas>
      </div>
    </WebGlErrorBoundary>
  );
};

export default SoppelScene;
